import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { StoreBadges } from "./StoreBadges";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import { GET_THE_APP_QR_SRC } from "@/constants/urls";

/** All display copy in one block for the copy pass. */
const COPY = {
  eyebrow: "For Fivestar members & creators",
  headlineAccent: "Earn yours",
  headlineRest: "from the field & the sidelines.",
  lede: "Already with Fivestar? Sign in with the login you already have.",
  // The app has no sign-up: only approved members and creators can log in.
  badgesNote: "For current Fivestar members and creators.",
  newcomerLead: "New to Fivestar?",
  qrCaption: "Scan to download",
} as const;

const CYAN = "#6DDAEF";

const NEWCOMER_LINK =
  "font-semibold underline underline-offset-2 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded";

/**
 * Full-screen "Get the App" overlay, opened from the header CTA (and mobile
 * menu). The panel reuses the billboard language from GetTheAppSection —
 * near-black card, diagonal shards, cyan glow, italic caps — with XL store
 * badges. Dialog semantics mirror MobileMenu: portal, focus trap, Esc +
 * backdrop close, body scroll lock, focus restored to the opener on close.
 */
const GetTheAppModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => (isOpen ? <ModalBody onClose={onClose} /> : null);

const ModalBody = ({ onClose }: { onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.querySelector<HTMLElement>("button")?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      opener?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-label="Get the Fivestar app"
    >
      {/* Backdrop */}
      <button
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      />

      {/* Billboard-language panel */}
      <div
        ref={panelRef}
        className="relative w-full max-w-xl overflow-hidden rounded-[2rem] bg-[#0A0A0A] px-7 py-12 text-center shadow-[0_24px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10 animate-in fade-in zoom-in-95 duration-200 sm:px-12 sm:py-14"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-40 h-[160%] w-[80%] rotate-[18deg] bg-gradient-to-b from-white/[0.05] to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-50%] left-1/2 h-[90%] w-[90%] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
          style={{
            background: `radial-gradient(closest-side, ${CYAN}, transparent 70%)`,
          }}
        />

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
        >
          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
        </button>

        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: CYAN }}
            />
            {COPY.eyebrow}
          </span>

          <h2 className="mt-5 text-3xl font-black italic uppercase leading-[1.02] text-white sm:text-4xl">
            <span style={{ color: CYAN }}>{COPY.headlineAccent}</span>{" "}
            {COPY.headlineRest}
          </h2>

          <p className="mx-auto mt-4 max-w-sm text-sm text-zinc-400 sm:text-base">
            {COPY.lede}
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-6 sm:flex-row sm:items-stretch">
            <div className="flex flex-col items-center justify-center gap-3">
              <StoreBadges
                location="get_the_app_modal"
                badgeHeightClassName="h-14 sm:h-16"
                className="flex-col items-center gap-3 sm:items-start"
              />
              <span className="text-xs text-zinc-400">{COPY.badgesNote}</span>
            </div>
            {/* Desktop QR bridge: scan with a phone to reach its store. */}
            <div className="hidden select-none flex-col items-center justify-center gap-2 border-l border-white/10 pl-6 sm:flex">
              <div className="rounded-xl bg-white p-3">
                <img
                  src={GET_THE_APP_QR_SRC}
                  alt="QR code linking to fivestar.video/app"
                  width={104}
                  height={104}
                  className="h-[104px] w-[104px]"
                />
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                {COPY.qrCaption}
              </span>
            </div>
          </div>

          <p className="mt-8 border-t border-white/10 pt-6 text-sm text-white">
            {COPY.newcomerLead}{" "}
            <TrackedLink
              ctaId="become_a_member"
              ctaLocation="get_the_app_modal"
              to="/become-a-member"
              onClick={onClose}
              className={NEWCOMER_LINK}
              style={{ color: CYAN }}
            >
              Become a member
            </TrackedLink>{" "}
            or{" "}
            <TrackedLink
              ctaId="become_a_creator_apply"
              ctaLocation="get_the_app_modal"
              to="/become-a-creator/apply"
              onClick={onClose}
              className={NEWCOMER_LINK}
              style={{ color: CYAN }}
            >
              apply to become a creator
            </TrackedLink>
            .
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GetTheAppModal;
